import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Activity, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { StatusPage, Monitor } from '../types';

interface DailyUptime {
  day: string;
  uptime_percentage: number;
  total_checks: number;
}

type PublicPage = StatusPage & {
  monitors_data: Monitor[];
  history?: Record<string, DailyUptime[]>;
  history_days?: number;
  password_required?: boolean;
  password_error?: string;
};

/**
 * One bar per day of real check history.
 *
 * Days a monitor was not checked — before it existed, or while the platform was
 * down — are drawn as gaps rather than filled in. A status page that invents
 * history is worse than one that admits it has none, because the whole point of
 * it is being believed during an incident.
 */
function UptimeHistory({ days, history }: { days: number; history: DailyUptime[] }) {
  const byDay = new Map(history.map(d => [d.day, d]));

  // Oldest first, so the newest day is the rightmost bar.
  const slots = Array.from({ length: days }, (_, i) => {
    const date = new Date();
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCDate(date.getUTCDate() - (days - 1 - i));
    const key = date.toISOString().slice(0, 10);
    return { key, entry: byDay.get(key) };
  });

  const measured = history.reduce((sum, d) => sum + d.total_checks, 0);
  const up = history.reduce((sum, d) => sum + (d.uptime_percentage / 100) * d.total_checks, 0);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Activity size={12} />
          <span>Last {days} days</span>
        </div>
        <span>{measured > 0 ? `${((up / measured) * 100).toFixed(2)}% uptime` : 'No history yet'}</span>
      </div>
      <div className="uptime-bar">
        {slots.map(({ key, entry }) => {
          if (!entry) {
            return <div key={key} className="bar" title={`${key}: no data`} style={{ height: 24, opacity: 0.15 }} />;
          }
          const label = `${key}: ${entry.uptime_percentage.toFixed(2)}% over ${entry.total_checks} check${entry.total_checks === 1 ? '' : 's'}`;
          return (
            <div
              key={key}
              className={`bar ${entry.uptime_percentage >= 100 ? 'up' : 'down'}`}
              title={label}
              style={{ height: 24 }}
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * A status page reached either by slug (/status/acme) or, when it is served on
 * its own custom domain, by the hostname alone — there is no slug in the URL to
 * go on, so the server resolves the page from the Host header.
 */
export function PublicStatusPage({ byHost = false }: { byHost?: boolean }) {
  const { slug } = useParams<{ slug: string }>();
  const endpoint = byHost ? '/api/status-by-host' : `/api/status/${slug}`;

  // What has actually been sent, as opposed to what is being typed: keying the
  // query on this stops every keystroke from firing a request at the server.
  const [submittedPassword, setSubmittedPassword] = React.useState('');
  const [typedPassword, setTypedPassword] = React.useState('');

  // Custom fetcher for public API
  const { data: page, isLoading, error } = useQuery<PublicPage>({
    queryKey: [byHost ? 'public-status-by-host' : 'public-status', slug, submittedPassword],
    queryFn: async () => {
      // The password travels in a header, not the query string: a URL is
      // recorded by every proxy in between and by the visitor's own history.
      const res = await fetch(endpoint, {
        headers: submittedPassword ? { 'X-Status-Password': submittedPassword } : {},
      });

      if (res.status === 401) {
        const body = await res.json();
        return { ...body, password_required: true, password_error: body.error ?? 'Incorrect password' } as PublicPage;
      }
      if (!res.ok) throw new Error('Status page not found');

      // The endpoint nests the page under `status_page` and returns its
      // monitors alongside it; flatten so the view reads one object.
      const body = await res.json();
      if (body.password_required) return body as PublicPage;
      return {
        ...body.status_page,
        monitors_data: body.monitors ?? [],
        history: body.history ?? {},
        history_days: body.history_days,
      };
    },
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyItems: 'center', height: '100vh', background: 'var(--color-bg-primary)' }}>
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="login-page">
        <div className="login-card">
          <AlertCircle size={48} color="var(--color-down)" style={{ marginBottom: 16 }} />
          <h1>Status Page Not Found</h1>
          <p>The status page you are looking for does not exist or has been removed.</p>
          <a href="/" className="btn btn-primary" style={{ marginTop: 24 }}>Back to Home</a>
        </div>
      </div>
    );
  }

  if (page.password_required) {
    return (
      <div className="login-page">
        <form
          className="login-card"
          onSubmit={e => {
            e.preventDefault();
            setSubmittedPassword(typedPassword);
          }}
        >
          {page.logo_url && <img src={page.logo_url} alt={page.name} style={{ height: 40, marginBottom: 16 }} />}
          <h1>{page.name || 'Status Page'}</h1>
          <p>This status page is password protected.</p>
          <input
            type="password"
            className="form-input"
            aria-label="Password"
            value={typedPassword}
            onChange={e => setTypedPassword(e.target.value)}
            placeholder="Password"
            autoFocus
            style={{ marginTop: 16 }}
          />
          {page.password_error && (
            <div style={{ color: 'var(--color-down)', fontSize: 'var(--font-size-sm)', marginTop: 8 }}>
              {page.password_error}
            </div>
          )}
          <button type="submit" className="btn btn-primary" style={{ marginTop: 16 }} disabled={!typedPassword}>
            View status
          </button>
        </form>
      </div>
    );
  }

  // The server decides the window and says so in the response; this only
  // covers a payload that predates the field.
  const historyDays = page.history_days ?? 30;
  const allUp = page.monitors_data?.every(m => m.status === 'up');
  const someDown = page.monitors_data?.some(m => m.status === 'down');

  return (
    <div className="status-page-public" style={{ background: 'var(--color-bg-primary)', minHeight: '100vh', color: 'var(--color-text-primary)' }}>
      <header className="status-page-header">
        {page.logo_url && <img src={page.logo_url} alt={page.name} style={{ height: 48, marginBottom: 16 }} />}
        <h1 style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 800 }}>{page.name}</h1>
      </header>

      <div className={`status-page-overall ${allUp ? 'all-up' : someDown ? 'has-issues' : ''}`} 
           style={{ borderColor: allUp ? 'var(--color-up-border)' : 'var(--color-down-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          {allUp ? <CheckCircle size={24} /> : <AlertCircle size={24} />}
          <span>{allUp ? 'All Systems Operational' : someDown ? 'Partial System Outage' : 'System Issues Detected'}</span>
        </div>
      </div>

      {page.announcement && (
        <motion.div 
          className="card" 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }}
          style={{ marginBottom: 32, background: 'var(--color-accent-bg)', borderColor: 'var(--color-accent)' }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Announcement</div>
          <div style={{ fontSize: 'var(--font-size-sm)' }}>{page.announcement}</div>
        </motion.div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {page.monitors_data?.map((m, i) => (
          <motion.div 
            key={m.id} 
            className="card"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 600 }}>{m.name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className={`badge badge-${m.status}`}>{m.status.toUpperCase()}</span>
              </div>
            </div>
            
            <UptimeHistory days={historyDays} history={page.history?.[m.id] ?? []} />
          </motion.div>
        ))}
      </div>

      <footer style={{ marginTop: 64, textAlign: 'center', paddingBottom: 32, opacity: 0.5 }}>
        <div style={{ fontSize: 'var(--font-size-xs)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <Clock size={12} />
          <span>Last updated: {new Date().toLocaleTimeString()}</span>
        </div>
        <div style={{ marginTop: 8, fontSize: 'var(--font-size-xs)' }}>
          Powered by <a href="/" style={{ color: 'var(--color-accent)', fontWeight: 600 }}>Uptimer</a>
        </div>
      </footer>
    </div>
  );
}
